import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserSearchIcon, SendIcon, CheckCircle2Icon, CheckIcon } from 'lucide-react';
type Step = 'search' | 'find' | 'outreach';
export function OwnerFinderDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [findPhase, setFindPhase] = useState<'loading' | 'done'>('loading');
  const [outreachPhase, setOutreachPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const findTimerRef = useRef<NodeJS.Timeout | null>(null);
  const outreachTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
      if (outreachTimerRef.current) clearTimeout(outreachTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startFind = () => {
    setFindPhase('loading');
    if (findTimerRef.current) clearTimeout(findTimerRef.current);
    findTimerRef.current = setTimeout(() => setFindPhase('done'), 1400);
  };
  const startOutreach = () => {
    setOutreachPhase('loading');
    if (outreachTimerRef.current) clearTimeout(outreachTimerRef.current);
    outreachTimerRef.current = setTimeout(() => setOutreachPhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'find') startFind();
    if (step === 'outreach') startOutreach();
  };
  return <section className="py-24 bg-white" data-id="element-3668">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3669">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-3670">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-3671">
            See the local business{' '}
            <span className="text-blue-600" data-id="element-3672">owner finder</span> in action
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-3673">
            Search local businesses, find verified business owner contacts, and
            launch outreach — all from one platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-3674">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3675">
            <SearchIcon className="w-4 h-4" data-id="element-3676" />
            Search businesses
          </button>
          <button onClick={() => handleTabSwitch('find')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'find' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3677">
            <UserSearchIcon className="w-4 h-4" data-id="element-3678" />
            Find owner contacts
          </button>
          <button onClick={() => handleTabSwitch('outreach')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'outreach' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-3679">
            <SendIcon className="w-4 h-4" data-id="element-3680" />
            Launch outreach
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-3681">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3682">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3683">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3684">
                  Search businesses
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3685">
                  Find local businesses to target.
                </p>
                <div className="space-y-5" data-id="element-3686">
                  <div data-id="element-3687">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3688">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3689">
                      Restaurants
                    </div>
                  </div>
                  <div data-id="element-3690">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3691">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3692">
                      Austin, TX
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3693">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3694">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3695" />
                      Searching database...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3696">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3697" />
                      1,240 businesses found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3698">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3699">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3700" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3701">
                      Querying local business database...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-3702">
                    <div className="flex items-center justify-between mb-6" data-id="element-3703">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-3704">
                        Business results
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-3705">
                      {['Austin Taco Co.', 'Riverside Cafe', 'Texas BBQ Joint'].map((name, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between" data-id="element-3706">
                          <div data-id="element-3707">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-3708">
                              {name}
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-3709">
                              Austin, TX • Restaurant
                            </p>
                          </div>
                          <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-3710" />
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'find' && <motion.div key="find" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3711">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3712">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3713">
                  Find owner contacts
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3714">
                  Select target decision-makers.
                </p>
                <div className="space-y-5" data-id="element-3715">
                  <div data-id="element-3716">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3717">
                      Target titles
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3718">
                      Owner, Founder
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3719">
                  {findPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3720">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3721" />
                      Finding business owners...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3722">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3723" />
                      Owner contacts found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3724">
                {findPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3725">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3726" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3727">
                      Retrieving verified emails and phones...
                    </p>
                  </div>}
                {findPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-3728">
                    <div className="flex items-center justify-between mb-6" data-id="element-3729">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-3730">
                        Business owner contacts
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-3731">
                      {[{
                  name: 'David Smith',
                  title: 'Owner',
                  email: 'david@austintaco.com',
                  phone: '(512) 555-0199'
                }, {
                  name: 'Elena Rodriguez',
                  title: 'Founder',
                  email: 'elena@riversidecafe.com',
                  phone: '(512) 555-0288'
                }, {
                  name: 'Marcus Johnson',
                  title: 'Owner',
                  email: 'marcus@texasbbq.com',
                  phone: '(512) 555-0377'
                }].map((contact, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100" data-id="element-3732">
                          <div className="flex justify-between items-start mb-2" data-id="element-3733">
                            <div data-id="element-3734">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-3735">
                                {contact.name}
                              </h4>
                              <p className="text-xs text-slate-500" data-id="element-3736">
                                {contact.title}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-3" data-id="element-3737">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-md" data-id="element-3738">
                              <CheckIcon className="w-3 h-3 text-blue-600" data-id="element-3739" />{' '}
                              {contact.email}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-md" data-id="element-3740">
                              <CheckIcon className="w-3 h-3 text-green-600" data-id="element-3741" />{' '}
                              {contact.phone}
                            </span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'outreach' && <motion.div key="outreach" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-3742">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-3743">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3744">
                  Launch outreach
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-3745">
                  Send emails to business owners.
                </p>
                <div className="space-y-5" data-id="element-3746">
                  <div data-id="element-3747">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3748">
                      Campaign name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 border-2 border-blue-600" data-id="element-3749">
                      Austin Restaurants
                    </div>
                  </div>
                  <div data-id="element-3750">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3751">
                      Sender account
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-3752">
                      alex@yourcompany.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-3753">
                  {outreachPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-3754">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-3755" />
                      Launching sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-3756">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-3757" />
                      Campaign launched
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-3758">
                {outreachPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-3759">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-3760" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-3761">
                      Preparing emails for local business owners...
                    </p>
                  </div>}
                {outreachPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center justify-center min-h-[350px] text-center" data-id="element-3762">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-3763">
                      <SendIcon className="w-8 h-8 text-green-600" data-id="element-3764" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3765">
                      Outreach active!
                    </h3>
                    <p className="text-slate-500 mb-8 max-w-sm" data-id="element-3766">
                      Your sequence is now sending to 1,240 verified local
                      business owner emails.
                    </p>
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block max-w-xs mx-auto" data-id="element-3767">
                      View campaign stats
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}